// src/pages/Accounting/Receipts/components/Dashboard/ReceiptRow.tsx

import { useState } from "react";
import type { Receipt } from "../../types";
import { PALETTE, SOURCE_LABELS } from "../../constants";
import { formatCurrency, formatDate } from "../../utils";

interface ReceiptRowProps {
  receipt: Receipt;
  category?: string;
  onClick: () => void;
}

export function ReceiptRow({ receipt, category, onClick }: ReceiptRowProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "80px 1fr 110px 150px 170px 50px",
        gap: 16,
        padding: "14px 0",
        borderBottom: `1px solid ${PALETTE.lineSoft}`,
        alignItems: "center",
        fontSize: 13,
        cursor: "pointer",
        background: hovered ? "#F3F5F5" : "transparent",
      }}
    >
      <div
        style={{
          color: PALETTE.ink2,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 500,
        }}
      >
        {formatDate(receipt.date)}
      </div>

      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: PALETTE.ink }}>
          {receipt.vendorName || "Reading receipt..."}
        </div>
        <div
          style={{
            fontSize: 12,
            color: PALETTE.ink3,
            fontWeight: 500,
            marginTop: 2,
          }}
        >
          {SOURCE_LABELS[receipt.source]}
        </div>
      </div>

      <div
        style={{
          textAlign: "right",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: PALETTE.ink,
        }}
      >
        {receipt.matchStatus === "processing"
          ? "\u2014"
          : formatCurrency(receipt.total, receipt.currency)}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          fontSize: 13,
          color: statusColorFor(receipt.matchStatus),
          fontWeight: receipt.matchStatus === "needs-review" ? 600 : 500,
        }}
      >
        <StatusDot status={receipt.matchStatus} />
        {statusTextFor(receipt.matchStatus)}
      </div>

      <div
        style={{
          color: category ? PALETTE.ink2 : PALETTE.ink3,
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {category || "Uncategorized"}
      </div>

      <div
        style={{
          color: hovered ? PALETTE.velaDeep : PALETTE.ink3,
          fontSize: 13,
          textAlign: "right",
          fontWeight: hovered ? 600 : 500,
        }}
      >
        View
      </div>
    </div>
  );
}

function statusTextFor(status: Receipt["matchStatus"]): string {
  switch (status) {
    case "matched":
      return "Matched";
    case "needs-review":
      return "Needs review";
    case "unmatched":
      return "Unmatched";
    case "processing":
      return "Processing";
  }
}

function statusColorFor(status: Receipt["matchStatus"]): string {
  switch (status) {
    case "matched":
      return PALETTE.ink2;
    case "needs-review":
      return PALETTE.amberInk;
    case "unmatched":
      return PALETTE.ink3;
    case "processing":
      return PALETTE.ink3;
  }
}

function StatusDot({ status }: { status: Receipt["matchStatus"] }) {
  if (status === "matched") {
    return (
      <span
        aria-hidden="true"
        style={{
          width: 11,
          height: 11,
          borderRadius: "50%",
          background: PALETTE.vela,
          color: "#fff",
          fontWeight: 700,
          fontSize: 7,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        V
      </span>
    );
  }
  if (status === "needs-review") {
    return (
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: PALETTE.amberInk,
        }}
      />
    );
  }
  if (status === "processing") {
    return (
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: PALETTE.ink3,
        }}
      />
    );
  }
  return null;
}